import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { VerdictBadge, Spinner } from '@/components/common';
import { ProductDialog } from '@/components/ProductDialog';
import { toast } from 'sonner';
import { Plus, MoreVertical, Info, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Funnel-coded column top borders (early cool -> testing warm -> scaling vivid -> dead muted)
const COLUMN_TINTS = {
  brain_dump: 'border-t-slate-400/50',
  product_idea: 'border-t-violet-400/70',
  researching: 'border-t-indigo-400/70',
  qualified: 'border-t-blue-400/70',
  store_build: 'border-t-cyan-400/70',
  creative_prep: 'border-t-teal-400/70',
  ready_to_launch: 'border-t-amber-400/70',
  live_testing: 'border-t-orange-400/70',
  optimizing: 'border-t-lime-400/70',
  scaling: 'border-t-emerald-400',
  killed: 'border-t-rose-500/40',
  archived: 'border-t-slate-500/40',
};

export default function Pipeline() {
  const [products, setProducts] = useState(null);
  const { stagesData } = useApp();
  const [dragId, setDragId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get('/products');
    setProducts(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const moveStage = async (product, stage) => {
    try {
      const { data } = await api.put(`/products/${product.id}/stage`, { stage });
      if (data.automation === 'roadmap_created') {
        toast.success('Qualified! Test roadmap + starter tasks auto-created', { duration: 5000 });
      } else {
        toast.success(`Moved to ${stagesData.stages.find((s) => s.key === stage)?.label || stage}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
    }
  };

  const onDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || dragId;
    const p = products?.find((x) => x.id === id);
    if (p && p.stage !== stage) moveStage(p, stage);
    setDragId(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted — roadmap, tasks and metrics cleaned up`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  // Group products by stage once per data change instead of filtering per column on every render
  const itemsByStage = useMemo(() => {
    const grouped = {};
    for (const p of products || []) {
      (grouped[p.stage] = grouped[p.stage] || []).push(p);
    }
    return grouped;
  }, [products]);

  if (!products) return <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Product Pipeline</h1>
          <div className="page-accent-bar" />
          <p className="mt-2 text-sm text-muted-foreground">Idea → test → scale or kill. Drag cards or use the menu to move stages.</p>
        </div>
        <ProductDialog onCreated={() => load()} trigger={
          <Button data-testid="pipeline-add-product-button" className="accent-btn gap-2"><Plus className="h-4 w-4" /> Add Product</Button>
        } />
      </div>

      <div data-testid="pipeline-kanban-board" className="flex gap-3 overflow-x-auto pb-4">
        {(stagesData.stages || []).map((stage) => {
          const items = itemsByStage[stage.key] || [];
          return (
            <div key={stage.key} data-testid="pipeline-kanban-column" className="w-[280px] shrink-0"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, stage.key)}>
              <Card className={cn('border-t-2 p-2', COLUMN_TINTS[stage.key] || 'border-t-slate-400/40')}>
                <div className="flex items-center justify-between px-1 pb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide">{stage.label}</span>
                    <span className="rounded bg-muted px-1.5 text-xs font-mono tabular-nums text-muted-foreground">{items.length}</span>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid="pipeline-stage-info-button" aria-label={`${stage.label} criteria`} className="text-muted-foreground hover:text-foreground"><Info className="h-3.5 w-3.5" /></button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 text-xs" align="start">
                      <p className="font-semibold">{stage.label}</p>
                      <p className="mt-2"><span className="font-semibold text-muted-foreground">Entry:</span> {stage.entry_criteria}</p>
                      <p className="mt-1.5 font-semibold text-muted-foreground">Required actions:</p>
                      <ul className="mt-0.5 list-disc space-y-0.5 pl-4">{stage.required_actions.map((a) => <li key={a}>{a}</li>)}</ul>
                      <p className="mt-1.5"><span className="font-semibold text-muted-foreground">Exit:</span> {stage.exit_criteria}</p>
                      <p className="mt-1.5 rounded bg-primary/10 p-2 text-primary"><span className="font-semibold">AI tip:</span> {stage.ai_tip}</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="min-h-[120px] space-y-2">
                  {items.map((p) => (
                    <div key={p.id} data-testid="pipeline-kanban-card" draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', p.id); setDragId(p.id); }}
                      onClick={() => navigate(`/products/${p.id}`)}
                      className="glass-tile cursor-pointer rounded-lg p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-tight">{p.name}</p>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <button data-testid="pipeline-card-menu-button" aria-label="Move product" className="text-muted-foreground hover:text-foreground"><MoreVertical className="h-4 w-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            {(stagesData.stages || []).filter((s) => s.key !== p.stage).map((s) => (
                              <DropdownMenuItem key={s.key} data-testid={`pipeline-move-${s.key}`} onClick={() => moveStage(p, s.key)}>Move to {s.label}</DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem data-testid="pipeline-card-delete-button" onClick={() => setDeleteTarget(p)}
                              className="text-rose-500 focus:text-rose-500">
                              <Trash2 className="mr-2 h-4 w-4" /> Delete product…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        {p.score_status === 'scoring' ? (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> scoring...</span>
                        ) : p.score ? (
                          <>
                            <span className="font-mono text-sm font-bold tabular-nums">{p.score.total_score}</span>
                            <VerdictBadge verdict={p.score.verdict} />
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">unscored</span>
                        )}
                      </div>
                      {p.next_action && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">→ {p.next_action}</p>}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes its roadmap, tasks, metrics, creatives and AI analysis. Linked notes are kept but unlinked, and your logged time history stays intact. This can't be undone.
              {deleteTarget && ['live_testing', 'optimizing', 'scaling'].includes(deleteTarget.stage) && (
                <span className="mt-2 block font-medium text-amber-600 dark:text-amber-400">Heads up: this product is in an active test. If the test simply ended, move it to Killed instead — Delete erases the record entirely.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pipeline-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="pipeline-delete-confirm" onClick={confirmDelete} disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
